import { useEffect, useState } from 'react';
import { API_BASE_URL, getReachability, hostOf, onReachabilityChange, type ReachabilityState } from '../api/client.ts';
import type { ConnectionState, RealtimeClient } from '../api/realtime.ts';
import { deriveOutage, type DerivedOutage } from '../features/ops/connectionStatus.ts';

/**
 * MVP-483. The one subscription to "can this surface still reach the backend", for the three places
 * that each had their own partial answer: the cockpit shell (`App.tsx`), the Stage window
 * (`StageRoot.tsx`) and the guest phone (`RequestPage.tsx`).
 *
 * Three signals, one reading — the same precedence `deriveConnectionStatus` has always used, now
 * reachable from surfaces that never had it:
 *   - `navigator.onLine`, via the window's own `online`/`offline` events;
 *   - REST reachability (`api/client.ts`), which as of this task the guest route finally moves too
 *     (`guestFetch`'s `markReachable`/`markUnreachable`);
 *   - the SSE connection state of whichever client this surface owns.
 *
 * `client` is `null` for a surface with no realtime client of its own. That is the Stage, by design
 * — `App.tsx` starts the shared `realtimeClient` for the console route only, and the guest page
 * builds its own unauthenticated one — so `null` is a real, supported argument rather than a
 * not-ready placeholder, and it can only ever produce `'none'` or `'app'` scope (see `OutageInput`).
 *
 * Nothing here queues, retries or replays a write. It reports; the surfaces decide what to cover.
 */
export function useConnectionHealth(client: RealtimeClient | null): DerivedOutage {
  const [realtimeState, setRealtimeState] = useState<ConnectionState | null>(() => client?.getState() ?? null);
  const [reachability, setReachability] = useState<ReachabilityState>(getReachability);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  useEffect(() => {
    if (!client) {
      // A surface that just lost its client (the guest page tearing one down on session loss) must
      // fall back to "no stream to judge", not keep the last state that client was in forever.
      setRealtimeState(null);
      return undefined;
    }
    // Read once on subscribe: `onStateChange` only fires on a CHANGE, so a client that connected
    // before this effect ran would otherwise leave the hook on its mount-time snapshot.
    setRealtimeState(client.getState());
    return client.onStateChange(setRealtimeState);
  }, [client]);

  useEffect(() => onReachabilityChange(setReachability), []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return deriveOutage({
    realtimeState,
    reachability,
    online,
    // The client knows the host its stream dials; without one, the REST base URL is the only host
    // this surface ever talks to. `hostOf` is api/client.ts's single copy of this — see its own
    // doc comment on why the offline copy must name the same host whichever layer failed.
    targetHost: client ? client.getTargetHost() : hostOf(API_BASE_URL),
  });
}
